import { AdvancedFilter, FilterConditionOutput, FilterStructure } from '@shared/components/ui/AdvancedFilter';
import { Search } from '@shared/components/ui/Search';
import { ChangeEvent, useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { FiltersApiAdapter } from '../../adapters/FiltersApiAdapter';
import { TeamsApiAdapter } from '../../adapters/TeamsApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { ColumnDef } from '@tanstack/react-table';
import { useAuthStore } from '@shared/stores/authStore';
import { getInvoicesViewFilterOptions } from '../../config/filterOptions';
import { TENANT_EXTERNAL_SHIPPING_TABLE_FIELDS } from '../../config/externalShippingColumns';
import { DownloadExcelButton } from './DownloadExcelButton';
import { useCountries } from '../../hooks/useCountries';

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(conditions.map(({ column, action, value }) => ({ column, action, value })));

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
  columns: ColumnDef<any>[];
  selectedColumns: string[];
  onColumnsChange: (newSelected: string[]) => void;
  filterConditions: FilterConditionOutput[];
  hasTeamData?: boolean;
  searchTerm: string;
  invoiceId?: number;
  searchFilter?: { filters: any[]; logic: string };
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  isStorageType?: boolean;
}

export const FiltersSection = ({
  handleSearch,
  onFilterChange,
  columns,
  selectedColumns,
  onColumnsChange,
  filterConditions,
  hasTeamData = false,
  searchTerm,
  invoiceId,
  searchFilter,
  sorting,
  isStorageType = false,
}: FiltersSectionProps) => {
  const { t } = useTranslation('invoicesViewModule');
  const { t: tUI } = useTranslation('ui');
  const teamType = useAuthStore((state) => state.team?.type);
  const hideColumnSelector = teamType === 'tenant_external' && !isStorageType;

  // ðŸŽ¯ Estado local - NO causa re-render del componente padre
  const [inputValue, setInputValue] = useState(searchTerm);

  // 🎯 Cargar países para el filtro
  const { countries } = useCountries();

  // ðŸŽ¯ FunciÃ³n para cargar opciones dinÃ¡micamente segÃºn la columna seleccionada
  const loadColumnValues = useCallback(async (columnValue: string) => {
    try {
      const httpClient = createHttpClient(apiClientWH);
      const filtersAdapter = new FiltersApiAdapter(httpClient);

      switch (columnValue) {
        case 'shop_id': {
          const shops = await filtersAdapter.getShops();
          return shops.map((shop) => ({
            label: shop.name,
            value: shop.id,
          }));
        }
        case 'payment_method_id': {
          const paymentMethods = await filtersAdapter.getPaymentMethods();
          return paymentMethods.map((method) => ({
            label: method.name,
            value: method.id,
          }));
        }
        // ðŸ›’ 'product_id' ahora usa values: 'product' - manejado automÃ¡ticamente por AdvancedFilter
        case 'courier_id': {
          const couriers = await filtersAdapter.getCouriers();
          return couriers.map((courier) => ({
            label: courier.name,
            value: courier.id,
          }));
        }
        case 'team_id': {
          const httpClient = createHttpClient(apiClientWH);
          const teamsAdapter = new TeamsApiAdapter(httpClient);
          const teams = await teamsAdapter.getTeamsChild();
          const options = teams.map((team) => ({
            label: team.name,
            value: team.id.toString(),
          }));
          return options;
        }
        default:
          return [];
      }
    } catch {
      // Silenciar errores en consola y devolver vacío (el UI ya maneja estados)
      return [];
    }
  }, []);

  // 🧠 Persistimos condiciones para controlar visibilidad estable del filtro Cliente
  const [localConditions, setLocalConditions] = useState<FilterConditionOutput[]>(filterConditions);

  // 🧠 Visibilidad del filtro Cliente: si hay datos o el usuario ya lo seleccionó
  const [showClientFilter, setShowClientFilter] = useState<boolean>(hasTeamData);
  useEffect(() => {
    if (hasTeamData) setShowClientFilter(true);
  }, [hasTeamData]);
  useEffect(() => {
    if (localConditions.some((c) => c.column === 'team_id')) {
      setShowClientFilter(true);
    }
  }, [localConditions]);
  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  useEffect(() => {
    setLocalConditions((prev) => {
      const prevSerialized = serializeConditions(prev);
      const nextSerialized = serializeConditions(filterConditions);
      return prevSerialized === nextSerialized ? prev : filterConditions;
    });
  }, [filterConditions]);

  // 🎯 Configuración de filtros para pedidos - importada desde configuración centralizada
  const filterOptions: FilterStructure[] = useMemo(
    () => getInvoicesViewFilterOptions(t, loadColumnValues, showClientFilter, countries),
    [t, loadColumnValues, showClientFilter, countries],
  );

  // ðŸŽ¯ Memoizar handler para evitar bucle infinito
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setLocalConditions(conditions);
      onFilterChange?.(conditions);
    },
    [onFilterChange],
  );

  // ðŸŽ¯ Handler para el cambio del input - Solo afecta al estado local
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // ðŸŽ¯ Handler para ejecutar bÃºsqueda - comunica al padre el filtro estructurado
  const handleSearchSubmit = () => {
    handleSearch(inputValue.trim());
  };

  return (
    <>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />
      {/* ðŸŽ¯ AdvancedFilter con traducciones automÃ¡ticas del namespace 'ui' */}
      <AdvancedFilter
        filterOptions={filterOptions}
        onChange={handleFilterChange}
        initialConditions={filterConditions}
        translations={{
          filters: tUI('advancedFilter.filters', 'Filtros'),
          fulfillsWith: tUI('advancedFilter.fulfillsWith', 'Cumple con'),
          selectColumn: tUI('advancedFilter.selectColumn', 'Seleccione una columna'),
          selectAction: tUI('advancedFilter.selectAction', 'Seleccione una acciÃ³n'),
          selectValue: tUI('advancedFilter.selectValue', 'Seleccione un valor'),
          addCondition: tUI('advancedFilter.addCondition', 'AÃ±adir condiciÃ³n'),
          notFound: tUI('advancedFilter.notFound', 'Sin resultados'),
          and: tUI('advancedFilter.and', 'Y'),
          or: tUI('advancedFilter.or', 'O'),
          enterText: tUI('advancedFilter.enterText', 'Escribir texto'),
          selectDate: tUI('advancedFilter.selectDate', 'Seleccionar fecha'),
          selectDateRange: tUI('advancedFilter.selectDateRange', 'Seleccionar rango'),
          from: tUI('advancedFilter.from', 'Desde'),
          to: tUI('advancedFilter.to', 'Hasta'),
        }}
      />
      {!hideColumnSelector && (
        <ColumnSelector
          columns={columns}
          selected={selectedColumns}
          onColumnsChange={onColumnsChange}
          storageKey="invoices-view-columns"
        />
      )}
      {!isStorageType && invoiceId != null && invoiceId > 0 && (
        <DownloadExcelButton
          invoiceId={invoiceId}
          search={searchTerm?.trim() ? searchTerm.trim() : undefined}
          searchFilter={searchFilter}
          sorting={sorting ?? undefined}
          selectedColumns={hideColumnSelector ? [...TENANT_EXTERNAL_SHIPPING_TABLE_FIELDS] : selectedColumns}
        />
      )}
    </>
  );
};
